import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FiCheck,
  FiEye,
  FiEyeOff,
  FiLock,
  FiMail,
  FiMapPin,
  FiPhone,
  FiSave,
  FiShield,
  FiUser,
  FiX,
} from 'react-icons/fi';
import axiosInstance from '../../utils/axiosInstance';
import { toast } from 'react-toastify';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { FormField, inputClassName } from '../../components/ui/FormField';
import { ErrorState, LoadingSkeleton } from '../../components/ui/States';
import { TwoFactorSecurity } from '../../components/Profile/TwoFactorSecurity';
import { AppTooltip } from '../../components/ui/AppTooltip';

const Profile = () => {
  const { t } = useTranslation();

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [homeAddress, setHomeAddress] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      setIsLoading(true);
      setError('');

      try {
        const response = await axiosInstance('/user/get_user_info', 'get');
        const data = response.data.user;

        if (!isMounted) return;
        setFirstName(data.firstName);
        setLastName(data.lastName);
        setPhoneNumber(data.phoneNumber);
        setHomeAddress(data.homeAddress);
        setEmail(data.email || '');
        setRole(data.role || '');
      } catch (err) {
        if (isMounted) setError(t('Unable to load profile'));
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [t]);

  const passwordRules = [
    { id: 'length', label: t('At least 8 characters'), isValid: /.{8,}/.test(password) },
    { id: 'uppercase', label: t('One uppercase letter'), isValid: /[A-Z]/.test(password) },
    { id: 'lowercase', label: t('One lowercase letter'), isValid: /[a-z]/.test(password) },
    { id: 'number', label: t('One number'), isValid: /[0-9]/.test(password) },
    { id: 'symbol', label: t('One special character'), isValid: /[!@#$%^&*(),.?":{}|<>]/.test(password) },
  ];

  const validateForm = () => {
    const nextErrors = {};

    if (!firstName.trim()) nextErrors.firstName = t('First Name is required');
    if (!lastName.trim()) nextErrors.lastName = t('Last Name is required');
    if (!phoneNumber.trim()) nextErrors.phoneNumber = t('Phone Number is required');
    if (!homeAddress.trim()) nextErrors.homeAddress = t('Home Address is required');

    const wantsPasswordUpdate = Boolean(password || confirmPassword);

    if (wantsPasswordUpdate) {
      const missingRule = passwordRules.find((rule) => !rule.isValid);
      if (missingRule) nextErrors.password = t('Password does not meet the security requirements.');
      if (!confirmPassword) nextErrors.confirmPassword = t('Confirm your new password');
      if (password && confirmPassword && password !== confirmPassword) {
        nextErrors.confirmPassword = t('Passwords do not match');
      }
    }

    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const clearFieldError = (field) => {
    setFieldErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (isSaving) return;

    if (!validateForm()) return;

    setIsSaving(true);
    try {
      const response = await axiosInstance('/user/update_user', 'put', {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phoneNumber: phoneNumber.trim(),
        homeAddress: homeAddress.trim(),
        password: password ? password : '',
      });

      if (response.status === 200) {
        toast.success(t('Updated successfully'));
        setPassword('');
        setConfirmPassword('');
      } else {
        toast.error(t(response?.data?.message || 'Unable to update profile'));
      }
    } catch (err) {
      toast.error(t('Unable to update profile'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className='mx-auto flex h-[calc(100dvh-120px)] min-h-[560px] w-full max-w-6xl flex-col overflow-hidden max-lg:h-[calc(100dvh-104px)] max-lg:min-h-0'>
      <div className='mb-4 shrink-0 text-center sm:mb-5'>
        <h1 className='text-2xl font-bold text-[var(--text-primary)] lg:text-3xl'>{t('My profile')}</h1>
        <p className='mx-auto mt-1 max-w-2xl text-sm leading-6 text-[var(--text-secondary)]'>
          {t('Update your personal information and password.')}
        </p>
      </div>

      {error && <ErrorState title={error} description={t('Try refreshing the page.')} />}

      {isLoading ? (
        <Card className='min-h-0 flex-1 p-5 sm:p-6'>
          <LoadingSkeleton rows={7} />
        </Card>
      ) : (
        <form onSubmit={onSubmit} className='grid min-h-0 flex-1 grid-rows-[minmax(0,1fr)_auto] gap-4'>
          <div className='grid min-h-0 gap-4 overflow-y-auto pb-1 lg:grid-cols-[minmax(0,1fr)_minmax(340px,0.82fr)]'>
            <Card className='flex min-h-0 flex-col overflow-hidden'>
              <div className='shrink-0 border-b border-[var(--border-subtle)] bg-[var(--surface-panel-muted)] p-4 sm:p-5'>
                <div className='flex flex-col items-center gap-3 text-center'>
                  <span className='flex h-12 w-12 shrink-0 items-center justify-center rounded-[var(--radius-md)] bg-[var(--brand-soft)] text-xl text-[var(--brand)]'>
                    <FiUser aria-hidden='true' />
                  </span>
                  <div>
                    <h2 className='text-lg font-bold text-[var(--text-primary)]'>{t('Account details')}</h2>
                    <p className='text-sm text-[var(--text-secondary)]'>{t('These details are used across the billing workspace.')}</p>
                  </div>
                  {role && <span className='inline-flex w-fit items-center rounded-full border border-[var(--border-subtle)] bg-[var(--surface-panel)] px-3 py-1 text-xs font-bold uppercase tracking-wide text-[var(--text-secondary)]'>{t(role === 'admin' ? 'Administrator' : 'Customer')}</span>}
                </div>
              </div>

              <div className='grid min-h-0 gap-3 overflow-y-auto p-4 sm:p-5 md:grid-cols-2 lg:content-start'>
                <FormField label={t('First Name')} required error={fieldErrors.firstName} errorId='profile-first-name-error'>
                  <input
                    className={inputClassName}
                    value={firstName}
                    autoComplete='given-name'
                    aria-invalid={Boolean(fieldErrors.firstName)}
                    aria-describedby={fieldErrors.firstName ? 'profile-first-name-error' : undefined}
                    onChange={(e) => { setFirstName(e.target.value); clearFieldError('firstName'); }}
                    required
                  />
                </FormField>
                <FormField label={t('Last Name')} required error={fieldErrors.lastName} errorId='profile-last-name-error'>
                  <input
                    className={inputClassName}
                    value={lastName}
                    autoComplete='family-name'
                    aria-invalid={Boolean(fieldErrors.lastName)}
                    aria-describedby={fieldErrors.lastName ? 'profile-last-name-error' : undefined}
                    onChange={(e) => { setLastName(e.target.value); clearFieldError('lastName'); }}
                    required
                  />
                </FormField>
                <FormField label={t('Phone Number')} required error={fieldErrors.phoneNumber} errorId='profile-phone-error'>
                  <div className='relative'>
                    <FiPhone className='pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]' aria-hidden='true' />
                    <input
                      className={`${inputClassName} pl-10`}
                      value={phoneNumber}
                      autoComplete='tel'
                      aria-invalid={Boolean(fieldErrors.phoneNumber)}
                      aria-describedby={fieldErrors.phoneNumber ? 'profile-phone-error' : undefined}
                      onChange={(e) => { setPhoneNumber(e.target.value); clearFieldError('phoneNumber'); }}
                      required
                    />
                  </div>
                </FormField>
                <FormField label={t('Home Address')} required error={fieldErrors.homeAddress} errorId='profile-address-error'>
                  <div className='relative'>
                    <FiMapPin className='pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]' aria-hidden='true' />
                    <input
                      className={`${inputClassName} pl-10`}
                      value={homeAddress}
                      autoComplete='street-address'
                      aria-invalid={Boolean(fieldErrors.homeAddress)}
                      aria-describedby={fieldErrors.homeAddress ? 'profile-address-error' : undefined}
                      onChange={(e) => { setHomeAddress(e.target.value); clearFieldError('homeAddress'); }}
                      required
                    />
                  </div>
                </FormField>
                {email && (
                  <div className='md:col-span-2'>
                    <p className='mb-1.5 text-sm font-semibold text-[var(--text-secondary)]'>{t('Email')}</p>
                    <div className='flex min-h-11 items-center gap-3 rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-panel-muted)] px-3 text-sm text-[var(--text-secondary)]'>
                      <FiMail className='text-[var(--text-muted)]' aria-hidden='true' />
                      <span className='truncate'>{email}</span>
                    </div>
                  </div>
                )}
              </div>
            </Card>

            <Card className='flex min-h-0 flex-col overflow-hidden'>
              <div className='shrink-0 border-b border-[var(--border-subtle)] bg-[var(--surface-panel-muted)] px-4 py-4 sm:px-5'>
                <div className='flex flex-col items-center gap-2 text-center'>
                  <span className='flex h-11 w-11 shrink-0 items-center justify-center rounded-[var(--radius-md)] bg-[var(--brand-soft)] text-lg text-[var(--brand)]'>
                    <FiShield aria-hidden='true' />
                  </span>
                  <div>
                    <h2 className='font-bold text-[var(--text-primary)]'>{t('Password security')}</h2>
                    <p className='text-sm text-[var(--text-secondary)]'>{t('Leave blank to keep your current password.')}</p>
                  </div>
                </div>
              </div>

              <div className='min-h-0 p-3 sm:p-4'>
                <div className='mx-auto max-w-md space-y-2'>
                  <div className='flex items-center justify-center gap-2 text-sm font-bold text-[var(--text-primary)]'>
                    <FiLock className='text-[var(--brand)]' aria-hidden='true' />
                    {t('Change Password')}
                  </div>

                  <FormField label={t('New Password')} error={fieldErrors.password} errorId='profile-password-error'>
                    <div className='relative'>
                      <input
                        type={isPasswordVisible ? 'text' : 'password'}
                        className={`${inputClassName} pr-11`}
                        name='password'
                        value={password}
                        autoComplete='new-password'
                        aria-invalid={Boolean(fieldErrors.password)}
                        aria-describedby={fieldErrors.password ? 'profile-password-error' : password ? 'profile-password-rules' : undefined}
                        onChange={(e) => { setPassword(e.target.value); clearFieldError('password'); }}
                      />
                      <AppTooltip label={isPasswordVisible ? t('Hide password') : t('Show password')}><button
                        type='button'
                        onClick={() => setIsPasswordVisible((prev) => !prev)}
                        className='absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-[var(--radius-sm)] text-[var(--text-muted)] transition-colors hover:bg-[var(--surface-panel-muted)] hover:text-[var(--text-primary)]'
                        aria-label={isPasswordVisible ? t('Hide password') : t('Show password')}
                      >
                        {isPasswordVisible ? <FiEyeOff aria-hidden='true' /> : <FiEye aria-hidden='true' />}
                      </button></AppTooltip>
                    </div>
                  </FormField>

                  <FormField label={t('Confirm Password')} error={fieldErrors.confirmPassword} errorId='profile-confirm-password-error'>
                    <input
                      type={isPasswordVisible ? 'text' : 'password'}
                      className={inputClassName}
                      value={confirmPassword}
                      autoComplete='new-password'
                      aria-invalid={Boolean(fieldErrors.confirmPassword)}
                      aria-describedby={fieldErrors.confirmPassword ? 'profile-confirm-password-error' : undefined}
                      onChange={(e) => { setConfirmPassword(e.target.value); clearFieldError('confirmPassword'); }}
                    />
                  </FormField>

                  {password && (
                    <div id='profile-password-rules' className='grid grid-cols-1 gap-x-3 gap-y-2 border-t border-[var(--border-subtle)] pt-3 text-xs text-[var(--text-secondary)] sm:grid-cols-2'>
                      {passwordRules.map((rule) => (
                        <span key={rule.id} className='flex items-center gap-2'>
                          <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${rule.isValid ? 'bg-green-500/10 text-[var(--success)]' : 'bg-[var(--surface-panel-muted)] text-[var(--text-muted)]'}`}>
                            {rule.isValid ? <FiCheck aria-hidden='true' /> : <FiX aria-hidden='true' />}
                          </span>
                          {rule.label}
                        </span>
                      ))}
                    </div>
                  )}
                  <TwoFactorSecurity isAdmin={role === 'admin'} />
                </div>
              </div>
            </Card>
          </div>

          <div className='shrink-0 pt-1 flex justify-center'>
            <Button type='submit' variant='primary' loading={isSaving} className='w-full sm:w-auto'>
              <FiSave aria-hidden='true' />
              {t('Update profile')}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};

export default Profile;
